import { createRouter, createWebHistory } from 'vue-router';
import axios from 'axios';
import { authManager } from '@/utils/auth-manager';
import { globalAuth } from '@/utils/global-auth';

const LoginPage = () => import('../components/LoginPage.vue');
const AuthCallback = () => import('../components/AuthCallback.vue');
const DashboardPage = () => import('../components/DashboardPage.vue');
const OrdersPage = () => import('../components/OrdersPage.vue');
const OrderDetail = () => import('../components/OrdersPageDetailsPopup.vue');
const ShipmentsPage = () => import('../components/ShipmentsPage.vue');
const GiveawaysPage = () => import('../components/GiveawaysPage.vue');
const UsersPage = () => import('../components/UsersPage.vue');
const KybPage = () => import('../components/KybPage.vue');
const ReferralsPage = () => import('../components/ReferralsPage.vue');
const UserDepositsPage = () => import('../components/UserDepositsPage.vue');
const SubaccountsPage = () => import('../components/SubaccountsPage.vue');
const SummaryPage = () => import('../components/SummaryPage.vue');
const CardKycPage = () => import('@/components/CardsKycPage.vue');
const CardsKycExportPage = () => import ('@/components/CardsKycExportPage.vue');
const LinkCardsPage = () => import ('@/components/LinkCardsPage.vue');
const UpdateSupplierPage = () => import('@/components/UpdateSupplierPage.vue');
const NotifyAccountChangePage = () => import('@/components/NotifyAccountChangePage.vue');
const FundCardsPage = () => import('@/components/FundCardsPage.vue');
const WithdrawalsPage = () => import('@/components/WithdrawalsPage.vue');
const NotifyUserPage = () => import('@/components/NotifyUserPage.vue');
const PayaccsysFunding = () => import('@/components/PayaccsysFunding.vue');
const AuditPage = () => import('@/components/AuditPage.vue');


const routes = [
	{ path: '/', redirect: '/dashboard' },
	{ path: '/login', component: LoginPage, meta: { requiresAuth: false } },
	{ path: '/auth/callback', component: AuthCallback, meta: { requiresAuth: false } },
	{
		path: '/dashboard',
		component: DashboardPage,
		meta: { requiresAuth: true },
		children: [
			{ path: 'summary', component: SummaryPage, meta: { requiresAuth: true } },
			{ path: 'audit', component: AuditPage, meta: { requiresAuth: true } },
			{ path: 'orders', component: OrdersPage, meta: { requiresAuth: true } },
			{ path: 'orders/:id', component: OrderDetail, meta: { requiresAuth: true } },
			{ path: 'shipments', component: ShipmentsPage, meta: { requiresAuth: true } },
			{ path: 'giveaways', component: GiveawaysPage, meta: { requiresAuth: true } },
			{ path: 'users', component: UsersPage, meta: { requiresAuth: true } },
			{ path: 'b2b', component: KybPage, meta: { requiresAuth: true } },
			{ path: 'referrals', component: ReferralsPage, meta: { requiresAuth: true } },
			{ path: 'b2b/:email/deposits', component: UserDepositsPage, meta: { requiresAuth: true } },
			{ path: 'subaccounts', component: SubaccountsPage, meta: { requiresAuth: true } },
			{ path: 'cards-kyc', component: CardKycPage, meta: { requiresAuth: true } },
			{ path: 'cards-kyc-export', component: CardsKycExportPage, meta: { requiresAuth: true } },
			{ path: 'link-cards', component: LinkCardsPage, meta: { requiresAuth: true } },
			{ path: 'update-suppliers', component: UpdateSupplierPage, meta: { requiresAuth: true } },
			{ path: 'notify-account-changes', component: NotifyAccountChangePage, meta: { requiresAuth: true } },
			{ path: 'fund-cards', component: FundCardsPage, meta: { requiresAuth: true } },
			{ path: 'withdrawals', component: WithdrawalsPage, meta: { requiresAuth: true } },
			{ path: 'notify-user', component: NotifyUserPage, meta: { requiresAuth: true } },
			{ path: 'payaccsys-funding', component: PayaccsysFunding, meta: { requiresAuth: true } },
			{ path: '', redirect: '/dashboard/summary' },
		],
	},
];

const router = createRouter({
	history: createWebHistory(), 
	routes,
});

const siteBackend = process.env.VUE_APP_API_SITE_BACKEND;

let authCache = {
	isAuthenticated: false,
	timestamp: 0,
	cacheDuration: 60000
};

window.authCache = authCache;

const CARDS_ROUTES = [
	'/dashboard/cards-kyc',
	'/dashboard/cards-kyc-export', 
	'/dashboard/link-cards',
	'/dashboard/update-suppliers',
	'/dashboard/notify-account-changes',
	'/dashboard/fund-cards'
];

router.beforeEach(async (to, from, next) => {
	if (to.meta.requiresAuth) {
		const now = Date.now();
		const isDashboardNav = from.path.startsWith('/dashboard') && to.path.startsWith('/dashboard');
		const isCacheValid = (now - authCache.timestamp) < authCache.cacheDuration;
		
		const isCardsRoute = CARDS_ROUTES.some(route => to.path.startsWith(route));
		
		if (isCardsRoute && !authManager.hasValidTokens()) {
			globalAuth.handleSessionExpired('Cards authentication required');
			return;
		}
		
		if (isDashboardNav && isCacheValid && authCache.isAuthenticated) {
			next();
			return;
		}
		
		try {
			const response = await axios.get(`${siteBackend}/api/admin/user`, {
				withCredentials: true
			});
			
			if (response.data.success) {
				authCache.isAuthenticated = true;
				authCache.timestamp = now;
				next();
			} else {
				throw new Error('Invalid session');
			}
		} catch (error) {
			if (error.response?.status === 429) {
				if (isCacheValid && authCache.isAuthenticated) {
					next();
					return;
				}
			}
			
			// Clear auth cache, tokens, and user info
			authCache.isAuthenticated = false;
			authCache.timestamp = 0;
			authManager.clearTokens(); // Clear JWT tokens for Backend 2
			sessionStorage.removeItem('currentUser');
			
			console.log('Authentication check failed, redirecting to login');
			
			// Only try to logout if not rate limited
			if (error.response?.status !== 429) {
				try {
					await axios.post(`${siteBackend}/api/admin/logout`, {}, {
						withCredentials: true
					});
				} catch (logoutError) {
					console.debug('Logout error:', logoutError);
				}
			}
			
			next('/login');
		}
	} else {
		next();
	}
});

export default router;
